import MaximizeIcon from "@assets/images/icon-maximize.svg?react";
import MinimizeIcon from "@assets/images/icon-minimize.svg?react";
import type { BFFArticleBodyElement } from "@src/app/types/bff";
import { useState } from "react";

import Button from "../../Button/Button";
import OptimisedImage from "../../OptimisedImage/OptimisedImage";
import ArticleImageCaption from "./ArticleImageCaption";

export default function ArticleImage({
  element,
}: {
  element: BFFArticleBodyElement["image"];
}) {
  const [isLightboxEnabled, setIsLightboxEnabled] = useState(false);

  const enableLightbox = () => {
    setIsLightboxEnabled(true);

    document.body.style.overflow = "hidden";
    document.body.style.height = "100%";

    // Prevent pull-to-refresh on Android
    document.documentElement.style.overscrollBehavior = "none";
    document.body.style.overscrollBehavior = "none";
  };

  const disableLightbox = () => {
    setIsLightboxEnabled(false);

    document.body.style.overflow = "auto";
    document.body.style.height = "100%";

    // Restore default overscroll
    document.documentElement.style.overscrollBehavior = "";
    document.body.style.overscrollBehavior = "";
  };

  return (
    <div>
      <figure className="pb-4">
        <div className="relative">
          <Button
            variant="secondary"
            size="medium"
            icon={<MaximizeIcon />}
            className="absolute top-0 right-0 z-10 m-2 block lg:hidden"
            onClick={enableLightbox}
          />

          <OptimisedImage
            filters={{
              lg: { f: "webp", w: 720, q: 80, dpr: 3 },
              md: { f: "webp", w: 720, q: 80, dpr: 3 },
              sm: { f: "webp", w: 360, q: 80, dpr: 3 },
            }}
            rootClassName="w-full hover:cursor-pointer"
            src={element.url}
            alt={element.altText || element.caption}
            fetchPriority="high"
            onClick={enableLightbox}
          />
        </div>

        <ArticleImageCaption element={element} />
      </figure>

      {isLightboxEnabled && (
        <div
          data-testid={`lightbox-${element.altText}`}
          className="bg-background-primary-on-dark fixed top-0 right-0 bottom-0 left-0 z-150 block h-screen w-screen overflow-scroll"
        >
          <div className="m-3 flex justify-end">
            <Button
              variant="secondary"
              size="large"
              icon={<MinimizeIcon />}
              className="float-right justify-end"
              onClick={disableLightbox}
            />
          </div>

          <OptimisedImage
            filters={{
              lg: { f: "webp", w: 720, q: 80, dpr: 3 },
              md: { f: "webp", w: 720, q: 80, dpr: 3 },
              sm: { f: "webp", w: 360, q: 80, dpr: 3 },
            }}
            rootClassName="w-full"
            src={element.url}
            alt={element.altText || element.caption}
            fetchPriority="high"
          />

          <ArticleImageCaption element={element} isLightbox />
        </div>
      )}
    </div>
  );
}
